import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/zafaraApi";
import { useShop } from "@/context/ShopContext";

export default function Inscription() {
  const { user, register } = useShop();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/catalogue";
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", password: "",
    pickup_city: "", pickup_quartier: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) navigate(next, { replace: true }); }, [user, next, navigate]);

  useEffect(() => {
    api.cities().then((r) => setCities(r?.cities || [])).catch(() => {});
  }, []);

  const quartierList = cities.find((c) => c.name === form.pickup_city)?.quartiers || [];

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("Le mot de passe doit faire au moins 6 caractères.");
    setBusy(true);
    try {
      await register({ ...form, email: form.email.trim().toLowerCase() });
      toast.success("Compte créé — bienvenue sur Zafara !");
      navigate(next, { replace: true });
    } catch (err) {
      toast.error(err.message || "Impossible de créer le compte.");
    } finally { setBusy(false); }
  };

  return (
    <section className="mx-auto max-w-lg px-5 py-12 md:py-20">
      <Helmet>
        <title>Créer un compte — Zafara Bénin</title>
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      <div className="text-center">
        <UserPlus className="h-10 w-10 mx-auto text-[#0E7A3B]" />
        <h1 className="mt-6 font-display text-4xl text-[#16241B] tracking-tighter">Créer un compte</h1>
        <p className="mt-3 text-[#16241B]/70">
          Un seul compte pour le site et l'application mobile Zafara.
        </p>
      </div>

      <form onSubmit={submit} className="mt-10 space-y-4 rounded-3xl border border-[#EDE7D9] bg-white p-6 md:p-8">
        <label className="block">
          <span className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60">Nom complet *</span>
          <input
            data-testid="signup-name"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="mt-2 w-full rounded-xl border border-[#EDE7D9] bg-[#FBFAF6] px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#0E7A3B]"
          />
        </label>
        <label className="block">
          <span className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60">Numéro WhatsApp *</span>
          <input
            data-testid="signup-phone"
            required
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="+229 01 XX XX XX XX"
            className="mt-2 w-full rounded-xl border border-[#EDE7D9] bg-[#FBFAF6] px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#0E7A3B]"
          />
        </label>
        <label className="block">
          <span className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60">Email *</span>
          <input
            data-testid="signup-email"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="mt-2 w-full rounded-xl border border-[#EDE7D9] bg-[#FBFAF6] px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#0E7A3B]"
          />
        </label>
        <label className="block">
          <span className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60">Mot de passe * (6+)</span>
          <input
            data-testid="signup-password"
            type="password"
            required
            minLength={6}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="mt-2 w-full rounded-xl border border-[#EDE7D9] bg-[#FBFAF6] px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#0E7A3B]"
          />
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60">Ville</span>
            <select
              data-testid="signup-city"
              value={form.pickup_city}
              onChange={(e) => setForm({ ...form, pickup_city: e.target.value, pickup_quartier: "" })}
              className="mt-2 w-full rounded-xl border border-[#EDE7D9] bg-[#FBFAF6] px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#0E7A3B]"
            >
              <option value="">Sélectionner…</option>
              {cities.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60">Quartier</span>
            <select
              data-testid="signup-quartier"
              value={form.pickup_quartier}
              onChange={(e) => setForm({ ...form, pickup_quartier: e.target.value })}
              disabled={!quartierList.length}
              className="mt-2 w-full rounded-xl border border-[#EDE7D9] bg-[#FBFAF6] px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#0E7A3B] disabled:opacity-60"
            >
              <option value="">{quartierList.length ? "Sélectionner…" : "Choisissez une ville"}</option>
              {quartierList.map((q) => <option key={q} value={q}>{q}</option>)}
            </select>
          </label>
        </div>
        <p className="text-xs text-[#16241B]/60">
          En créant un compte, vous acceptez les conditions générales de Zafara.
        </p>
        <button
          data-testid="signup-submit"
          type="submit"
          disabled={busy}
          className="w-full mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#F5A800] text-[#16241B] px-6 py-3.5 font-display font-bold shadow-[0_15px_40px_rgba(245,168,0,0.35)] hover:bg-[#0E7A3B] hover:text-white transition-all disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
          {busy ? "Création…" : "Créer mon compte"}
        </button>
        <p className="text-center text-sm text-[#16241B]/70 pt-2">
          Déjà inscrit ?{" "}
          <Link to={`/compte/connexion${next !== "/catalogue" ? `?next=${next}` : ""}`} className="text-[#0E7A3B] font-semibold hover:underline">
            Se connecter
          </Link>
        </p>
      </form>
    </section>
  );
}
